/**
 * Kiểu dữ liệu kho bản OCR của module `ocr` (`/api/v1/m/ocr/ocr-files…`, đợt 4).
 *
 * Chép đúng hợp đồng của `modules/ocr/backend/src/ocr-file/`: tên trường giữ `snake_case`.
 */
import type { DocStatus } from '../xu-ly/types';

/** Trạng thái gộp của một tài liệu theo các bản OCR của nó (`fileState`). */
export const OCR_FILE_STATES = ['READY', 'RUNNING', 'FAILED', 'EMPTY'] as const;
export type OcrFileState = (typeof OCR_FILE_STATES)[number];

export const TRANSCRIPT_STATUSES = ['QUEUED', 'READING', 'DONE', 'FAILED'] as const;
export type TranscriptStatus = (typeof TRANSCRIPT_STATUSES)[number];

export type TranscriptOrigin = 'PRE_OCR' | 'DOCUMENT' | 'BACKFILL';

/** Chứng từ đang dùng một bản OCR (chỉ có ở chi tiết). */
export interface OcrVersionDocument {
  id: string;
  file_name: string | null;
  schema_code: string;
  schema_name: string;
  status: DocStatus;
  created_at: string;
}

/** Một bản OCR (`ocr_transcript`) của tài liệu. */
export interface OcrVersion {
  id: string;
  status: TranscriptStatus;
  origin: TranscriptOrigin;
  reader_key: string | null;
  provider: string | null;
  model_label: string | null;
  page_count: number | null;
  pages_done: number | null;
  error: string | null;
  pinned_at: string | null;
  created_by: string;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  origin_document_id: string | null;
  /** Tên model đọc, lùi về nhà cung cấp rồi khoá. */
  reader_label: string | null;
  /** Đọc thẳng lớp văn bản của PDF gốc-số, không gọi model. */
  is_text_layer: boolean;
  attached_count: number;
  /** Bản dùng khi tạo chứng từ: bản ghim, không thì bản xong mới nhất. */
  is_effective: boolean;
  /** Câu backend giải thích vì sao chưa xoá được; `null` = xoá được. */
  delete_blocked_reason: string | null;
  documents?: OcrVersionDocument[];
}

/** Một tài liệu trong kho (`ocr_source_file`) kèm các bản OCR. */
export interface OcrFile {
  id: string;
  file_name: string | null;
  file_size: number | null;
  mime_type: string | null;
  created_by: string;
  created_at: string;
  state: OcrFileState;
  version_count: number;
  attached_count: number;
  effective: OcrVersion | null;
  running: OcrVersion | null;
  delete_blocked_reason: string | null;
  versions: OcrVersion[];
}

export interface OcrFilePage {
  items: OcrFile[];
  next_cursor: string | null;
}

export type OcrUploadOutcome = 'QUEUED' | 'SKIPPED_DONE' | 'SKIPPED_RUNNING' | 'REJECTED' | 'FAILED';

/** `POST /ocr-files/upload` (`202`): một kết quả mỗi tệp. */
export interface OcrUploadResult {
  results: Array<{
    file_name: string;
    outcome: OcrUploadOutcome;
    source_file_id?: string;
    version_id?: string;
    message?: string;
  }>;
}
